import { Typography } from '@automata/ui';
import Box from '@mui/material/Box';
import { DataGridPro, GridRowOrderChangeParams } from '@mui/x-data-grid-pro';
import { FC, useCallback } from 'react';

import type { ReorderableDataGridProps } from './types';
import { getReorderedItems } from './utils';

const DEFAULT_ROW_HEIGHT = 45;
const DEFAULT_EMPTY_MESSAGE = 'No items to display';

/**
 * A reusable DataGrid component with drag-and-drop reordering capabilities
 *
 * @example
 * ```tsx
 * const columns = [
 *   { field: 'name', headerName: 'Name', flex: 1 },
 *   { field: 'actions', headerName: '', width: 60, renderCell: (params) => <EditButton /> }
 * ];
 *
 * <ReorderableDataGrid
 *   data={items}
 *   columns={columns}
 *   onReorder={setItems}
 * />
 * ```
 */
export const ReorderableDataGrid: FC<ReorderableDataGridProps> = ({
  data,
  columns,
  onReorder,
  rowHeight = DEFAULT_ROW_HEIGHT,
  emptyStateMessage = DEFAULT_EMPTY_MESSAGE,
  emptyStateComponent,
  sx,
  getRowId,
  showHeaders = false,
  showBorders = false,
}) => {
  const handleRowOrderChange = useCallback(
    ({ oldIndex, targetIndex }: GridRowOrderChangeParams) => {
      const reordered = getReorderedItems(data, oldIndex, targetIndex);
      if (reordered) {
        onReorder(reordered);
      }
    },
    [data, onReorder]
  );

  // Show empty state when no data
  if (data.length === 0) {
    if (emptyStateComponent) {
      return <Box>{emptyStateComponent}</Box>;
    }

    return (
      <Box>
        <Typography variant="body2" color="text.secondary">
          {emptyStateMessage}
        </Typography>
      </Box>
    );
  }

  const defaultSx = {
    border: showBorders ? undefined : 'none',
    '& .MuiDataGrid-columnHeaders': {
      display: showHeaders ? undefined : 'none',
    },
    '& .MuiDataGrid-row': {
      transition: 'all 0.2s',
      '&:hover': {
        backgroundColor: 'action.hover',
      },
      '&.Mui-selected': {
        backgroundColor: 'transparent',
      },
    },
    '& .MuiDataGrid-cell': {
      border: 'none',
      padding: 0.25,
      justifyContent: 'flex-start',
      alignContent: 'center',
      '&:focus': {
        outline: '2px solid',
        outlineColor: 'primary.main',
        outlineOffset: '-2px',
      },
      '&:focus-within': {
        outline: '2px solid',
        outlineColor: 'primary.main',
        outlineOffset: '-2px',
      },
    },
    '& .MuiDataGrid-columnHeader': {
      padding: '1 1.5',
      '&:focus': {
        outline: 'none',
      },
      '&:focus-within': {
        outline: 'none',
      },
    },
    '& .MuiDataGrid-scrollbar': {
      display: 'none',
    },
    ...sx,
  };

  return (
    <DataGridPro
      rows={data}
      columns={columns}
      rowReordering
      onRowOrderChange={handleRowOrderChange}
      disableColumnSorting
      disableColumnMenu
      hideFooter
      rowSelection={false}
      rowHeight={rowHeight}
      getRowId={getRowId}
      sx={defaultSx}
    />
  );
};
